import { Order, OrderType } from '@/models/Order';
import { OrderStatus } from '@/models/types/OrderStatus';
import {
  fetchAllAssignedOrders,
  fetchOrderById,
  putOrderDetails,
  startOrder as startOrderRequest,
  saveOrderStatus,
} from '@/api/order/OrderManagement';
import { Pinia, Store } from '@/store/pinia-class-component';
import { useEntitiesStore } from '@/store/entities/entitiesStore';
import { Roomgroup } from '@/models';
import { instanceToInstance, plainToInstance } from 'class-transformer';
import {
  EntityTransaction,
  EntityTransactionType,
  OrderChanges,
  TransactionDiff,
} from '@/models/OrderChanges';
import { Entity } from '@/models/types/Entity';
import { generateDiff } from '@/utils/diff/diff';
import { DifferenceChange } from '@/utils/diff/Difference';
import { usePersistenceStore } from '@/store/persistence/persistenceStore';
import { useLayoutStore, WorkMode } from '@/store/layout/layoutStore';
import { useImagesStore } from '@/store/images/imageStore';
import { getAnalyticsProvider } from '@/plugins/bxAnalytics';
import { useUserStore } from '@/store/user/userStore';
import { useRoomGroupStore } from '@/store/entities/roomGroupStore';
import { useBuildingStore } from '@/store/entities/buildingStore';
import { Compatibility } from '@/models/Compatibility';
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';

export function useOrderStore() {
  return new OrderStore();
}

@Store
export class OrderStore extends Pinia {
  private _orders: Array<Order>;
  private _activeOrder: Order | undefined;
  private _orderType?: OrderType;
  private _isAutoSaving: boolean;
  private _orderConfirmationDialogDisplayed = false;

  constructor() {
    super();
    this._orders = [];
    this._activeOrder = undefined;
    this._orderType = undefined;
    this._isAutoSaving = false;
  }

  private getAnalytics() {
    return getAnalyticsProvider();
  }

  get isAutoSaving() {
    return this._isAutoSaving;
  }

  setIsAutoSaving(value: boolean) {
    this._isAutoSaving = value;
  }

  get orderType() {
    return this._orderType;
  }

  get hasUnsavedOrderChanges() {
    const orderChanges = this.getOrderChanges();
    return orderChanges?.currentHash !== orderChanges?.lastSavedHash;
  }

  setOrderType(type?: OrderType) {
    this._orderType = type;
  }

  isOnSiteInspection() {
    return this.orderType === OrderType.ON_SITE_INSPECTION;
  }

  isInstallationOrder() {
    return this.orderType === OrderType.MONT;
  }

  isTkdOrder() {
    return this.orderType === OrderType.TKD;
  }

  setOrderConfirmationDialogDisplayed(status: boolean): void {
    this._orderConfirmationDialogDisplayed = status;
  }

  get hasOrderConfirmationDialogDisplayed(): boolean {
    return this._orderConfirmationDialogDisplayed;
  }

  get orders(): Array<Order> {
    return this._orders.sort((a: Order, b: Order) => {
      if (a.appointment.start && b.appointment.start) {
        return (
          Date.parse(a.appointment.start) - Date.parse(b.appointment.start)
        );
      }
      return 0;
    });
  }

  get activeOrderId(): string | undefined {
    return this._activeOrder?.orderId;
  }

  get activeOrder(): Order | undefined {
    return this._activeOrder;
  }

  get pendingOrders() {
    return this.orders.filter(
      (order: Order) => order.status === OrderStatus.OPEN
    );
  }

  get activeOrders() {
    return this.orders.filter(
      (order: Order) => order.status === OrderStatus.IN_PROGRESS
    );
  }

  get closedOrders() {
    return this.orders.filter(
      (order: Order) => order.status === OrderStatus.CLOSED
    );
  }

  get selectedOrder() {
    return this._activeOrder;
  }

  setOrders(orders: Array<Order>) {
    this._orders = orders;
  }

  getOrderById(orderId: string) {
    return this._orders.find((order: Order) => order.id === orderId);
  }

  async fetchAllOrders() {
    this._orders = await fetchAllAssignedOrders();
  }

  async fetchOrderById(orderId: string) {
    this._orders.push(await fetchOrderById(orderId));
  }

  clearOrders() {
    this._orders = [];
    this._activeOrder = undefined;
  }

  async startOrder(id: string): Promise<Order | undefined> {
    const order = this._orders.find(
      (orderElement: Order) => orderElement.id === id
    );

    if (!order) {
      console.error(`Can't find order with id: ${id}`);
      return undefined;
    }

    const activeOrder = await usePersistenceStore().getActiveOrder(order.id);

    if (!activeOrder) {
      usePersistenceStore().setActiveOrder(order);
    }

    const mapPlainDiffsToClass = (
      diffs: TransactionDiff[] | undefined,
      transactionUserId?: string
    ) =>
      diffs?.map(
        (diff) =>
          new TransactionDiff(
            diff?.oldValue,
            diff?.newValue,
            (diff as any)?.path?.split?.('.') ?? (diff as any)?.path ?? [],
            (diff as any)?.type,
            (diff as any)?.userId ?? transactionUserId
          )
      ) ?? [];

    const mapPlainTransactionsToClass = (transactions: Record<string, any>) =>
      Object.entries(transactions).reduce(
        (mappedTransactions, [key, value]) => {
          const diff = mapPlainDiffsToClass(value?.diff, value?.userId);
          // Fallback: if transaction-level userId missing, try to take from first diff
          const fallbackUserId =
            Array.isArray(diff) && diff.length > 0
              ? (diff[0] as any)?.userId
              : undefined;
          const transactionUserId = value?.userId ?? fallbackUserId;
          mappedTransactions[key] = new EntityTransaction(
            value?.entity,
            value?.entityType,
            value?.entityId,
            value?.type,
            value?.timestamp,
            diff,
            value?.originalEntity,
            transactionUserId
          );
          return mappedTransactions;
        },
        {} as Record<string, EntityTransaction>
      );

    this._activeOrder = order;
    this._activeOrder.changes = plainToInstance(
      OrderChanges,
      activeOrder?.changes ?? order?.changes ?? {}
    );
    this._activeOrder.changes.transactions = mapPlainTransactionsToClass(
      this._activeOrder.changes.transactions
    );

    this.setOrderConfirmationDialogDisplayed(false);
    this.setOrderType(order.workTypeCode);

    if (
      order.status !== OrderStatus.IN_PROGRESS &&
      useLayoutStore().workMode === 'ORDER'
    ) {
      try {
        const startedOrder = await startOrderRequest(id);
        const startedChanges = startedOrder?.changes;

        const hydrateArrayChange = (
          key: 'roomGroupProgress' | 'tkdServices' | 'unverifiedEntities'
        ) => {
          const localValue = this._activeOrder?.changes?.[key] ?? [];
          const remoteValue = startedChanges?.[key] ?? [];

          if (localValue.length === 0 && remoteValue.length > 0) {
            this._activeOrder!.changes[key] = [...remoteValue];
            return true;
          }

          return false;
        };

        const localTransactions =
          this._activeOrder?.changes?.transactions ?? {};
        const remoteTransactions = startedChanges?.transactions ?? {};
        const shouldHydrateTransactions =
          Object.keys(localTransactions).length === 0 &&
          Object.keys(remoteTransactions).length > 0;

        order.status = startedOrder?.status ?? OrderStatus.IN_PROGRESS;
        order.modifiedAt = startedOrder?.modifiedAt;
        order.installerAppointment =
          startedOrder?.installerAppointment ?? order.installerAppointment;

        if (this._activeOrder?.changes) {
          const changesHydrated = [
            hydrateArrayChange('roomGroupProgress'),
            hydrateArrayChange('tkdServices'),
            hydrateArrayChange('unverifiedEntities'),
          ].some(Boolean);

          if (shouldHydrateTransactions) {
            this._activeOrder.changes.transactions =
              mapPlainTransactionsToClass(remoteTransactions);
          }

          if (changesHydrated || shouldHydrateTransactions) {
            await this._activeOrder.changes.onOrderChangesUpdate();
            usePersistenceStore().updateOrderChanges(
              this._activeOrder.id,
              this._activeOrder.changes
            );
          }
        }
      } catch (e) {
        console.error(e);
        return undefined;
      }
    }
    return this._activeOrder;
  }

  clearActiveOrderInfo() {
    if (this._activeOrder) {
      const businessEntityId = this._activeOrder.businessEntityId;
      useEntitiesStore().removeBusinessEntityFromList(businessEntityId);
      usePersistenceStore().clearLocalOrder(
        businessEntityId,
        this._activeOrder.id
      );

      const order = this._orders.find(
        (order) => order.id === this._activeOrder?.id
      );
      if (order) {
        order.status = OrderStatus.CLOSED;
      }
    }
    this._activeOrder = undefined;
  }

  async finishOrder(note?: string, propertyCompatibility?: Compatibility) {
    if (!this._activeOrder) {
      console.error(`Can't find order ${this._activeOrder}`);
      return;
    }
    const orderId = this._activeOrder.id;
    try {
      await putOrderDetails(
        orderId,
        note ?? '',
        propertyCompatibility ?? new Compatibility()
      );
      await saveOrderStatus(orderId, OrderStatus.CLOSED);
      this.clearActiveOrderInfo();
    } catch (error) {
      console.error('Failed to finish order:', error);
      throw error;
    }
  }

  toggleRoomGroupProgress(roomGroup: Roomgroup) {
    this.changeRoomGroupProgress(roomGroup);
  }

  isRoomGroupCompleted(roomGroup: Roomgroup) {
    return this._activeOrder?.changes.hasRoomGroupProgress(roomGroup.id);
  }

  changeRoomGroupProgress(roomGroup: Roomgroup) {
    if (!this._activeOrder) {
      return;
    }

    if (this._activeOrder?.changes.hasRoomGroupProgress(roomGroup.id)) {
      this._activeOrder?.changes.removeRoomGroupProgress(roomGroup.id);
    } else {
      this._activeOrder?.changes.addRoomGroupProgress(roomGroup.id);
    }

    usePersistenceStore().updateOrderChanges(
      this._activeOrder.id,
      this._activeOrder.changes
    );
  }

  removeRoomgroupProgress(roomGroup: Roomgroup) {
    if (!this._activeOrder) {
      return;
    }
    if (this._activeOrder?.changes.hasRoomGroupProgress(roomGroup.id)) {
      this._activeOrder?.changes.removeRoomGroupProgress(roomGroup.id);
    }

    usePersistenceStore().updateOrderChanges(
      this._activeOrder.id,
      this._activeOrder.changes
    );
  }

  private trackAutoSave(saveSuccess: boolean, errorMessage?: string) {
    const analytics = this.getAnalytics();
    const payload = {
      saveSuccess,
      errorMessage: errorMessage || null,
      orderId: this._activeOrder?.id || undefined,
      propertyId: this._activeOrder?.propertyId || undefined,
      businessEntityId: this._activeOrder?.businessEntityId || undefined,
      installerId: this._activeOrder?.installerId || undefined,
      timestamp: new Date().toISOString(),
    };
    analytics?.captureEvent('auto_save', payload);
  }

  triggerAutoSave() {
    const layout = useLayoutStore();
    if (!layout.isOnline) return;
    // Disable autosave in deep link mode
    if (layout.workMode === WorkMode.DEEP_LINK) return;
    if (!this._activeOrder) {
      return this.onActiveOrderNotFound();
    }
    const orderChanges = this._activeOrder.changes;
    if (orderChanges.unsavedTransactionCount > 10 && !this.isAutoSaving) {
      this.setIsAutoSaving(true);
      const waitingForUserToIdle = setInterval(async () => {
        if (layout.isUserIdle) {
          clearInterval(waitingForUserToIdle);
          await this.autoSave();
        }
      }, 1000);
    }
  }

  async onActiveOrderNotFound() {
    if (await useUserStore().isUserDeveloper()) {
      console.warn('No active order found but user is developer');
      return false;
    } else {
      throw new Error('No active order found');
    }
  }

  async autoSave() {
    try {
      await usePersistenceStore().saveData(true);
      await useImagesStore().uploadImages();
      this.setIsAutoSaving(false);
      this.trackAutoSave(true);
      useLayoutStore().resetIsUserIdle();
      console.info('Auto save successful');
    } catch (error) {
      console.error('Error saving data:', error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      this.trackAutoSave(false, errorMessage);
      this.setIsAutoSaving(false);
    }
  }

  handleSaveEntityTransactions(entity: Entity) {
    if (!this._activeOrder) return;

    const changes: OrderChanges = this._activeOrder.changes;

    if (entity.type === 'building') {
      changes.removeUnverifiedEntity('building', entity.id);
    }

    const entityId = entity.id;
    const existingTransaction = changes.transactions[entityId];
    const existingEntity = useEntitiesStore().hasEntityById(entityId)
      ? useEntitiesStore().getEntityById(entityId)
      : undefined;
    const currentUserId = useUserStore().userAttributes?.id;
    if (!existingEntity && !existingTransaction) {
      changes.upsertTransaction(
        entity,
        EntityTransactionType.CREATE_ENTITY,
        [],
        instanceToInstance(entity),
        currentUserId
      );
      this.finalizeTransactionUpdate();
      return;
    }

    const transactionType =
      existingTransaction?.type ?? EntityTransactionType.UPDATE_ENTITY;

    let originalEntity = existingEntity;

    if (existingTransaction?.originalEntity) {
      originalEntity = existingTransaction.originalEntity;
    } else if (transactionType === EntityTransactionType.CREATE_ENTITY) {
      originalEntity = entity;
    }

    const originalEntityCloned = instanceToInstance(originalEntity);
    const newEntityCloned = instanceToInstance(entity);

    const currentDiff = generateDiff(
      originalEntityCloned,
      newEntityCloned
    ).filter((diff) => !(diff.type === 'CREATE' && diff.value === undefined));

    if (
      this._activeOrder?.workTypeCode === OrderType.TKD &&
      newEntityCloned instanceof InstallationPoint
    ) {
      const TKDService = newEntityCloned.services;
      if (TKDService?.length > 0)
        this._activeOrder?.changes?.addTKDServiceId(newEntityCloned.id);
    }

    if (
      currentDiff.length === 0 &&
      existingTransaction?.type !== EntityTransactionType.CREATE_ENTITY
    ) {
      console.debug(
        'Transaction reverted, deleting transaction for entity:',
        entityId
      );
      delete changes.transactions[entityId];
      this.finalizeTransactionUpdate();
      return;
    }

    changes.upsertTransaction(
      entity,
      transactionType,
      currentDiff as DifferenceChange[],
      originalEntityCloned,
      currentUserId
    );
    this.finalizeTransactionUpdate();
  }

  private finalizeTransactionUpdate() {
    this._activeOrder?.changes.addUnsavedTransaction();
    this.saveOrderChanges().then(() =>
      console.debug('Order save changes updated')
    );
    this.triggerAutoSave();
  }

  handleDeleteEntityTransactions(entity: Entity) {
    if (!this._activeOrder) {
      return;
    }

    const changes = this._activeOrder.changes;

    const isEntityAlreadyInTransaction = !!changes.transactions[entity.id];
    const newEntityInTransaction = useEntitiesStore().hasEntityById(entity.id);

    if (isEntityAlreadyInTransaction) {
      delete changes.transactions[entity.id];
    } else if (newEntityInTransaction) {
      const currentUserId = useUserStore().userAttributes?.id;
      this._activeOrder.changes.upsertTransaction(
        entity,
        EntityTransactionType.DELETE_ENTITY,
        [],
        undefined,
        currentUserId
      );
    }
    this.finalizeTransactionUpdate();
  }

  async saveOrderChanges() {
    if (!this._activeOrder) {
      return this.onActiveOrderNotFound();
    }

    return this._activeOrder.changes.onOrderChangesUpdate().then(() => {
      if (!this._activeOrder) return;
      usePersistenceStore().updateOrderChanges(
        this._activeOrder.id,
        this._activeOrder.changes
      );
    });
  }

  updateLastSavedOnActiveOrder() {
    const activeOrder = this._activeOrder;
    if (!activeOrder) return;

    return activeOrder.changes.onOrderChangesUpdate().then(() => {
      activeOrder.changes.onSaveOrderChanges();

      usePersistenceStore().updateOrderChanges(
        activeOrder.id,
        activeOrder.changes
      );
    });
  }

  getOrderChanges() {
    if (this._activeOrder) {
      return this._activeOrder.changes;
    }
    return undefined;
  }

  getOrderAddresses() {
    const addresses = [];
    if (this._activeOrder?.primaryAddress) {
      addresses.push(this._activeOrder?.primaryAddress);
    }
    addresses.push(...(this._activeOrder?.additionalAddresses ?? []));
    return addresses;
  }

  hasTransaction(id: string, type?: EntityTransactionType): boolean {
    const transaction = useOrderStore().getOrderChanges()?.transactions[id];
    return type ? transaction?.type === type : transaction !== undefined;
  }

  isFirstInstallationOrder() {
    return this._activeOrder?.detail?.isFirstInstallation === true;
  }

  isSmartHeatingOrder() {
    if (!this._activeOrder) return false;
    return this._activeOrder.hasSmartHeatingDevices();
  }

  unfinishedSmartHeatingRoomGroups(): Map<string, Roomgroup> {
    if (
      !this._activeOrder?.hasSmartHeatingDevices() ||
      !this.hasOrderConfirmationDialogDisplayed ||
      !this.isFirstInstallationOrder()
    )
      return new Map<string, Roomgroup>();

    const accessibleRoomgroups = useBuildingStore()
      .getCompatibleBuildings()
      .filter((building) => building.isAccessible)
      .flatMap((building) =>
        useRoomGroupStore().getRoomGroupsByBuildingId(building.id)
      )
      .filter((rg) => rg.isAccessible);
    const orderChanges = this.activeOrder?.changes;

    const unfinished = new Map<string, Roomgroup>();
    for (const rg of accessibleRoomgroups.values()) {
      if (
        ['LIVING', 'BUSINESS'].includes(rg.usageType) &&
        !orderChanges?.hasRoomGroupProgress(rg.id)
      ) {
        unfinished.set(rg.id, rg);
      }
    }
    return unfinished;
  }

  hasTKDService(installationPointId: string) {
    return this._activeOrder?.changes?.hasTKDService(installationPointId);
  }

  get tkdOrderTicketNumbers() {
    if (!this._activeOrder || !this._activeOrder?.notes) return [];

    const regex = /#(\d+)(?:[^#\n]*?(VA\d{6,}))?/gi;
    const matches = [...this._activeOrder.notes.matchAll(regex)];

    return matches.map((m) => ({
      ticketNumber: m[1],
      deviceSerialNumber: m[2] ?? null,
    }));
  }
}
